/**
 * "Ekip Sistem Kullanımı" bölümünün gövdesi.
 *
 * Veriyi hesaplamaz — `useTeamActivity` çıktısını alır ve gösterir. Bölüm
 * başlığı, tarih filtresi ve aç/kapa düğmesi diğer bölümlerle aynı görünsün
 * diye üst sayfada (SMOLeadReports) kalır.
 */

import React, { useState, useMemo } from 'react';
import {
  FaCircle,
  FaClock,
  FaSignInAlt,
  FaUsers,
  FaUserClock,
  FaChevronDown,
  FaChevronUp,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSync,
  FaDownload,
  FaSpinner,
  FaExclamationTriangle,
} from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import * as XLSX from 'xlsx';
import styles from './TeamActivityPanel.module.css';
import {
  formatDuration,
  formatRelative,
  formatDateTime,
  formatDayLabel,
  toHours,
} from '../../utils/onlineActivity';
import { ACTIVITY_WINDOW_DAYS } from './useTeamActivity';

const CHART_COLORS = ['#4299e1', '#68d391', '#9f7aea', '#f6ad55', '#4fd1c5', '#fc8181'];
const TOP_EMPLOYEE_BARS = 12;

const TeamActivityPanel = ({ data, startDate, endDate, smoName }) => {
  const { rows, summary, dailyTotals, loading, error, reload, hasHistory, windowStartDay } = data;
  const [sortKey, setSortKey] = useState('rangeMs');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  const sortedRows = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'name') return direction * a.name.localeCompare(b.name, 'tr');
      if (sortKey === 'isOnline') return direction * ((a.isOnline ? 1 : 0) - (b.isOnline ? 1 : 0));
      return direction * ((a[sortKey] || 0) - (b[sortKey] || 0));
    });
  }, [rows, sortKey, sortDirection]);

  const topByTime = useMemo(
    () =>
      [...rows]
        .filter((r) => r.rangeMs > 0)
        .sort((a, b) => b.rangeMs - a.rangeMs)
        .slice(0, TOP_EMPLOYEE_BARS)
        .map((r) => ({
          name: r.name.length > 18 ? `${r.name.substring(0, 17)}…` : r.name,
          fullName: r.name,
          Saat: toHours(r.rangeMs),
        })),
    [rows]
  );

  const dailyChart = useMemo(
    () =>
      dailyTotals.map((d) => ({
        label: formatDayLabel(d.date),
        date: d.date,
        Saat: toHours(d.onlineMs),
        people: d.people,
      })),
    [dailyTotals]
  );

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <FaSort className={styles.sortIdle} />;
    return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const downloadExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(
      sortedRows.map((r) => ({
        'Çalışan': r.name,
        'E-posta': r.email,
        'Durum': r.isOnline ? 'Çevrimiçi' : 'Çevrimdışı',
        'Aralıkta Süre': formatDuration(r.rangeMs),
        'Aralıkta Süre (saat)': toHours(r.rangeMs),
        'Aktif Gün': r.activeDays,
        'Günlük Ortalama': formatDuration(r.avgActiveDayMs),
        'Giriş Sayısı': r.logins,
        'Oturum Sayısı': r.sessions,
        'Son Aktivite': formatDateTime(r.lastActivity),
        'Toplam Süre (tüm zamanlar)': formatDuration(r.totalOnlineMs),
      }))
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Sistem Kullanımı');
    const range = `${startDate || 'baslangic'}_${endDate || 'bugun'}`;
    XLSX.writeFile(book, `ekip-sistem-kullanimi_${(smoName || 'smo').replace(/\s+/g, '-')}_${range}.xlsx`);
  };

  if (loading && !hasHistory) {
    return (
      <div className={styles.stateBox}>
        <FaSpinner className={styles.spinner} />
        Kullanım verileri yükleniyor...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={styles.stateBox}>
        <FaExclamationTriangle className={styles.stateIcon} />
        Bu SMO altında çalışan bulunamadı.
      </div>
    );
  }

  return (
    <>
      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* ── Özet kutuları ── */}
      <div className={styles.tiles}>
        <div className={styles.tile}>
          <FaCircle className={`${styles.tileIcon} ${styles.tileIconOnline}`} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>
              {summary.onlineCount}
              <small> / {summary.teamSize}</small>
            </span>
            <span className={styles.tileLabel}>Şu An Çevrimiçi</span>
          </div>
        </div>
        <div className={styles.tile}>
          <FaClock className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>{formatDuration(summary.totalMs)}</span>
            <span className={styles.tileLabel}>Ekip Toplam Süre</span>
          </div>
        </div>
        <div className={styles.tile}>
          <FaUserClock className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>{formatDuration(summary.avgPerPersonMs)}</span>
            <span className={styles.tileLabel}>Kişi Başı Ortalama</span>
          </div>
        </div>
        <div className={styles.tile}>
          <FaSignInAlt className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>{summary.totalLogins}</span>
            <span className={styles.tileLabel}>Toplam Giriş</span>
          </div>
        </div>
        <div className={styles.tile}>
          <FaUsers className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>
              {summary.activeCount}
              <small> / {summary.teamSize}</small>
            </span>
            <span className={styles.tileLabel}>Aralıkta Aktif Kullanıcı</span>
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <span className={styles.toolbarNote}>
          Kullanım geçmişi son {ACTIVITY_WINDOW_DAYS} gün için tutulur (
          {new Date(windowStartDay).toLocaleDateString('tr-TR')} tarihinden itibaren).
        </span>
        <button className={styles.toolbarButton} onClick={reload} disabled={loading}>
          <FaSync className={loading ? styles.spinner : undefined} /> Yenile
        </button>
        <button className={styles.toolbarButton} onClick={downloadExcel}>
          <FaDownload /> Excel
        </button>
      </div>

      {/* ── Çalışan tablosu ── */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.sortable} onClick={() => toggleSort('name')}>Çalışan {sortIcon('name')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('isOnline')}>Durum {sortIcon('isOnline')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('rangeMs')}>Aralıkta Süre {sortIcon('rangeMs')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('activeDays')}>Aktif Gün {sortIcon('activeDays')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('avgActiveDayMs')}>Günlük Ort. {sortIcon('avgActiveDayMs')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('logins')}>Giriş {sortIcon('logins')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('lastActivity')}>Son Aktivite {sortIcon('lastActivity')}</th>
              <th className={styles.sortable} onClick={() => toggleSort('totalOnlineMs')}>Toplam {sortIcon('totalOnlineMs')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const expanded = expandedId === row.id;
              const maxDayMs = Math.max(1, ...row.days.map((d) => d.onlineMs));
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={styles.row}
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                  >
                    <td>
                      <span className={styles.nameCell}>
                        <span className={styles.name}>{row.name}</span>
                        <span className={styles.meta}>{row.email || '—'}</span>
                      </span>
                    </td>
                    <td>
                      {row.isOnline ? (
                        <span className={styles.statusOnline}>
                          <FaCircle /> Çevrimiçi
                          {row.currentSessionMs > 0 && <small> ({formatDuration(row.currentSessionMs)})</small>}
                        </span>
                      ) : (
                        <span className={styles.statusOffline}>Çevrimdışı</span>
                      )}
                    </td>
                    <td className={styles.numeric}>
                      <strong>{formatDuration(row.rangeMs)}</strong>
                    </td>
                    <td className={styles.numeric}>{row.activeDays}</td>
                    <td className={styles.numeric}>{formatDuration(row.avgActiveDayMs)}</td>
                    <td className={styles.numeric}>{row.logins}</td>
                    <td title={formatDateTime(row.lastActivity)}>{formatRelative(row.lastActivity)}</td>
                    <td className={styles.numeric}>{formatDuration(row.totalOnlineMs)}</td>
                    <td className={styles.chevron}>{expanded ? <FaChevronUp /> : <FaChevronDown />}</td>
                  </tr>

                  {expanded && (
                    <tr className={styles.detailRow}>
                      <td colSpan={9}>
                        <div className={styles.detailStats}>
                          <div className={styles.detailStat}>
                            <span className={styles.detailLabel}>Oturum (aralıkta)</span>
                            <span className={styles.detailValue}>{row.sessions}</span>
                          </div>
                          <div className={styles.detailStat}>
                            <span className={styles.detailLabel}>Toplam Oturum (tüm zamanlar)</span>
                            <span className={styles.detailValue}>{row.sessionCount}</span>
                          </div>
                          <div className={styles.detailStat}>
                            <span className={styles.detailLabel}>Ort. Oturum Süresi</span>
                            <span className={styles.detailValue}>
                              {formatDuration(row.sessionCount > 0 ? row.totalOnlineMs / row.sessionCount : 0)}
                            </span>
                          </div>
                          <div className={styles.detailStat}>
                            <span className={styles.detailLabel}>Son Aktivite</span>
                            <span className={styles.detailValue}>{formatDateTime(row.lastActivity)}</span>
                          </div>
                        </div>

                        <div className={styles.detailTitle}>Günlük Dağılım</div>
                        {row.days.length === 0 ? (
                          <div className={styles.emptyHint}>
                            Seçilen tarih aralığında kayıtlı kullanım süresi yok.
                          </div>
                        ) : (
                          <div className={styles.dayList}>
                            {row.days.map((day) => (
                              <div key={day.date} className={styles.dayItem}>
                                <span className={styles.dayLabel}>{formatDayLabel(day.date)}</span>
                                <span className={styles.dayTrack}>
                                  <span
                                    className={styles.dayBar}
                                    style={{ width: `${(day.onlineMs / maxDayMs) * 100}%` }}
                                  />
                                </span>
                                <span className={styles.dayValue}>{formatDuration(day.onlineMs)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Grafikler ── */}
      <div className={styles.chartGrid}>
        <div className={styles.chartCard}>
          <h4 className={styles.chartTitle}>Ekip Günlük Toplam Süre (saat)</h4>
          {dailyChart.length === 0 ? (
            <div className={styles.emptyHint}>Seçilen tarih aralığında kayıt yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis unit=" sa" />
                <Tooltip
                  formatter={(value, _key, item) => [`${value} saat · ${item?.payload?.people || 0} kişi`, 'Toplam']}
                />
                <Bar dataKey="Saat" fill="#4299e1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={styles.chartCard}>
          <h4 className={styles.chartTitle}>Çalışan Bazında Toplam Süre (saat)</h4>
          {topByTime.length === 0 ? (
            <div className={styles.emptyHint}>Seçilen tarih aralığında kayıt yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(280, topByTime.length * 34)}>
              <BarChart data={topByTime} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" unit=" sa" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip
                  labelFormatter={(label) => topByTime.find((t) => t.name === label)?.fullName || label}
                  formatter={(value) => [`${value} saat`, 'Süre']}
                />
                <Bar dataKey="Saat" radius={[0, 4, 4, 0]}>
                  {topByTime.map((entry, index) => (
                    <Cell key={entry.fullName} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <p className={styles.footNote}>
        Süreler, kullanıcı sisteme giriş yapmışken sekmenin açık kaldığı zamandan hesaplanır ve
        güne göre kaydedilir. Açık oturumun son birkaç dakikası henüz günlük toplama yansımamış
        olabilir. Takip yeni etkinleştirildiği için bu tarihten önceki günlerde kayıt bulunmaz.
      </p>
    </>
  );
};

export default TeamActivityPanel;
